import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscriptionTiers } from './useSubscriptionTiers';

interface MedicalRecordCounts {
  records: number;
  vaccinations: number;
  appointments: number;
  total: number;
}

export const useMedicalRecordLimits = (petId: string | undefined) => {
  const { user } = useAuth();
  const { data: tiers } = useSubscriptionTiers();

  // Get current counts for ALL user's pets (not just one pet)
  const { data: counts, isLoading: countsLoading } = useQuery({
    queryKey: ['medical-record-counts', user?.id],
    queryFn: async (): Promise<MedicalRecordCounts> => {
      if (!user?.id) return { records: 0, vaccinations: 0, appointments: 0, total: 0 };

      // Get all pets owned by this user
      const { data: userPets } = await supabase
        .from('pets')
        .select('id')
        .eq('user_id', user.id);

      if (!userPets || userPets.length === 0) {
        return { records: 0, vaccinations: 0, appointments: 0, total: 0 };
      }

      const petIds = userPets.map(p => p.id);

      // Count all medical records across ALL user's pets
      const [recordsResult, vaccinationsResult, appointmentsResult] = await Promise.all([
        supabase.from('medical_records').select('id', { count: 'exact', head: true }).in('pet_id', petIds),
        supabase.from('vaccinations').select('id', { count: 'exact', head: true }).in('pet_id', petIds),
        supabase.from('appointments').select('id', { count: 'exact', head: true }).in('pet_id', petIds),
      ]);

      const records = recordsResult.count || 0;
      const vaccinations = vaccinationsResult.count || 0;
      const appointments = appointmentsResult.count || 0;

      return {
        records,
        vaccinations,
        appointments,
        total: records + vaccinations + appointments,
      };
    },
    enabled: !!user?.id,
  });

  // Get user's subscription info including trial status and custom limits
  const { data: userSubscription, isLoading: userLoading } = useQuery({
    queryKey: ['user-subscription', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('users')
        .select('subscription_tier, premium_trial_ends_at, custom_medical_record_limit')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Check if user is in active premium trial
  const isInTrial = userSubscription?.subscription_tier === 'free' && 
                    userSubscription?.premium_trial_ends_at && 
                    new Date(userSubscription.premium_trial_ends_at) > new Date();

  const currentTier = tiers?.find(t => t.tier === userSubscription?.subscription_tier);
  
  // Priority: trial > custom admin override > tier default
  const customLimit = (userSubscription as any)?.custom_medical_record_limit;
  const maxRecords = isInTrial ? -1 : (customLimit != null ? customLimit : (currentTier?.max_medical_records_total ?? 0));
  const allowPrescriptions = isInTrial ? true : (currentTier?.allow_prescription_uploads ?? false);
  const advancedTracking = isInTrial ? true : (currentTier?.advanced_medical_tracking ?? false);
  const isUnlimited = maxRecords === -1;
  const currentCount = counts?.total ?? 0;
  const hasReachedLimit = !isUnlimited && currentCount >= maxRecords;
  const remainingRecords = isUnlimited ? -1 : Math.max(0, maxRecords - currentCount);
  
  // Calculate days remaining in trial
  const trialDaysRemaining = isInTrial && userSubscription?.premium_trial_ends_at
    ? Math.ceil((new Date(userSubscription.premium_trial_ends_at).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  return {
    counts,
    maxRecords,
    currentCount,
    remainingRecords,
    hasReachedLimit,
    isUnlimited,
    allowPrescriptions,
    advancedTracking,
    currentTier: userSubscription?.subscription_tier || 'free',
    isInTrial,
    trialDaysRemaining,
    trialEndsAt: userSubscription?.premium_trial_ends_at,
    isLoading: countsLoading || userLoading,
  };
};
